import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { PanelBar, PanelBarItem, PanelBarSelectEventArguments } from "@progress/kendo-react-layout";
import { Input } from "@progress/kendo-react-inputs";
import { Loader } from "@progress/kendo-react-indicators";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { AuthorizationProfile } from "../../../../types/authorization-profile/AuthorizationProfile";
import { TinyUser, User } from "../../../../types/user";
import CustomUserGridInfo from "../../../../components/custom/grid/CustomUserGridInfo";
import { UserAuthProfile } from "../../../../types/user/UserAuthProfile";
import { Button } from "@progress/kendo-react-buttons";
import usersService from "../../../../services/users.service";
import { Dictionary } from "../../../../types/Dictionary";
import useLocale from "../../../../hooks/useLocale";
import useSwal from "../../../../hooks/useSwal";
import LoadingOverlay from "../../../../components/LoadingOverlay";
import authorizationProfileService from "../../../../services/authorizationprofile.service";
import { sortUsersData } from "../../../../utils/userUtil";
import useTranslation from "../../../../hooks/useTranslation";
import InfiniteScroll from "react-infinite-scroll-component";
import { Page } from "../../../../types/Page";

interface UpsertProfileUsersDialogProps {
  profile: AuthorizationProfile;
  users: User[];
  freezeProfile : string;
  onCloseDialog: () => void;
  onProfileUserUpsert: (
    upsertedProfileUsers: User[],
    profile: AuthorizationProfile
  ) => void;
}

interface ProfileUser extends User {
  removeable?: boolean;
}

interface GroupedProfileUser {
  categoryName: string;
  users: ProfileUser[];
}

const initialDataState = {
  skip: 0,
  take: 16,
};

const UpsertProfileUsersDialog: React.FC<UpsertProfileUsersDialogProps> = ({
  profile,
  users,
  freezeProfile,
  onCloseDialog,
  onProfileUserUpsert,
}) => {
  const trans=useTranslation("UpsertProfileUsersDialog");
  const Swal = useSwal()
  const localeCtx = useLocale();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [assignedUsers, setAssignedUsers] = useState<ProfileUser[]>([]);
  const [constAssignedUsers, setConstAssignedUsers] = useState<ProfileUser[]>(
    []
  );
  const [updateButton, setUpdateButton] = useState<boolean>(true);
  const [unassignedUsers, setUnassignedUsers] = useState<ProfileUser[]>([]);
  const [groupedUnassignedUsers, setGroupedUnassignedUsers] = useState<
    GroupedProfileUser[]
  >([]);  
  const [assignedUserSearch, setAssignedUserSearch] = useState<string>("");
  const [unassignedUserSearch, setUnassignedUserSearch] = useState<string>("");
  const [unassignedUsersListManagement, setUnassignedUsersListManagement] =
    useState<GroupedProfileUser[]>([]);
  const [shownAssignedUsers, setShownAssignedUsers] = useState<ProfileUser[]>([]);
  const [shownUnassignedUsers, setShownUnassignedUsers] = useState<ProfileUser[]>([]);
  const [pageAssigned, setPageAssigned] = useState<Page>(initialDataState);
  const [pageUnassigned, setPageUnassigned] = useState<Page>(initialDataState);
  const [expandedGroup, setExpandedGroup] = useState<string[]>([]);
  const [addedUsers, setAddedUsers] = useState<ProfileUser[]>([]);

  useEffect(() => {
    trans.fetchTranslations("UpsertProfileUsersDialog");
  }, [localeCtx?.selectedLocale]);

  useEffect(() => {
    setAssignedUsers(
      users.filter((u) => u.authorizationProfile?.id === profile?.id)
    );
    setConstAssignedUsers(
      users.filter((u) => u.authorizationProfile?.id === profile?.id)
    );
    const tempUnassignedUsers = freezeProfile == "true" ? 
    users.filter((u) =>  u.authorizationProfile?.id !== profile?.id 
    && profile?.id != 1 && u.authorizationProfile?.id != 1).map((el) => {return { ...el, removeable: true };}) :
    users
      .filter((u) => u.authorizationProfile?.id !== profile?.id)
      .map((el) => {return { ...el, removeable: true }})

    setUnassignedUsers(tempUnassignedUsers);
  }, [profile, users]);

  useEffect(() => {
    var groupedpUsers: GroupedProfileUser[] = [];
    unassignedUsers
      .filter((el) => searchUser(el, unassignedUserSearch))
      .map((user) => {
        var authProfile =
          user.authorizationProfile !== undefined
            ? user.authorizationProfile.name
            : "No Permission Group";
        var index = groupedpUsers.findIndex(
          (gu) => gu?.categoryName === authProfile
        );
        if (index === -1) {
          var newGroup: GroupedProfileUser = {
            categoryName: authProfile,
            users: [user],
          };
          if (
            unassignedUsersListManagement &&
            unassignedUsersListManagement.length !== 0
          ) {
            const getIndex = unassignedUsersListManagement?.findIndex(
              (user) => user?.categoryName === authProfile
            );
            groupedpUsers[getIndex] = newGroup;
          } else {
            groupedpUsers.push(newGroup);
          }
        } else {
          groupedpUsers[index].users.push(user);
        }
      });

    if (groupedUnassignedUsers && groupedUnassignedUsers.length === 0) {
      setUnassignedUsersListManagement(groupedpUsers);
    }
    setGroupedUnassignedUsers(groupedpUsers);
    if (JSON.stringify(assignedUsers) !== JSON.stringify(constAssignedUsers)) {
      setUpdateButton(false);
    } else {
      setUpdateButton(true);
    }
  }, [unassignedUsers, unassignedUserSearch]);

  const handleAssignedUserSearchChange = (event: any) => {
    setAssignedUserSearch(event.value);
  };

  const handleUnassignedUserSearchChange = (event: any) => {
    setUnassignedUsersListManagement([]);
    setUnassignedUserSearch(event.value);
  };

  const searchUser = (userDetails: ProfileUser, searchTerm: string) => {
    var searchText = searchTerm.trim().toLocaleLowerCase();
    var firstName = userDetails.firstName ? userDetails?.firstName : "";
    var lastName = userDetails.lastName ? userDetails.lastName : "";
    var userName = `${firstName} ${lastName}`.trim();
    if (
      userDetails?.email?.toLocaleLowerCase().includes(searchText) ||
      userName?.toLocaleLowerCase().includes(searchText)
    ) {
      return true;
    } else {
      return false;
    }
  };

  const submitHandler = async () => {
    setLoading(true);
    var userAuthProfiles: UserAuthProfile[] = [];
    addedUsers.forEach((el) => {
      if (el.removeable !== undefined && el.removeable) {
        var _userAuthProfile: UserAuthProfile = {
          id: el.id,
          loginUserId: el.loginUserId,
          firstName: el.firstName ?? "",
          lastName: el.lastName ?? "",
          email: el.email ?? "",
          phoneNo: "",
          image: el.image ?? "",
          isArchived: el.isArchived,
          authorizationProfileId: profile.id,
        };
        userAuthProfiles.push(_userAuthProfile);
      }
    });
    usersService
      .updateUserAuthProfile(userAuthProfiles)
      .then((updatedUsers) => {
        setLoading(false);
        Swal.fire({
          icon: "success",
          title: trans.fetchLabelKeyTranslation(
            "UPGupdatedTitle",
            "Users Permission Group updated."
          ),
        });
        onProfileUserUpsert(assignedUsers, profile);
        onCloseDialog();
      })
      .catch((err) => {
        setLoading(false);
        if (err instanceof Error) {
          setError(
            trans.fetchLabelKeyTranslation(
              "SomethingError",
              "Something went wrong! Please try again"
            )
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleAddUser = (userDetails: ProfileUser) => {
    setAddedUsers([userDetails, ...addedUsers]);
    setAssignedUsers([userDetails, ...assignedUsers]);
    setUnassignedUsers(
      unassignedUsers.filter((ele) => ele.id !== userDetails.id)
    );
    if (groupedUnassignedUsers.length !== 0)
      setUnassignedUsersListManagement(groupedUnassignedUsers);
  };

  const handleDeleteUser = (userDetails: ProfileUser) => {
    setAddedUsers(addedUsers.filter(x => x.id !== userDetails.id));
    const tempUnassignedUsers = [...unassignedUsers, userDetails];
    const sortedUsers = sortUsersData([...tempUnassignedUsers])
    setUnassignedUsers(sortedUsers);
    setAssignedUsers(assignedUsers.filter((ele) => ele.id !== userDetails.id));
  };

  useEffect(()=>{
    setShownAssignedUsers(assignedUsers.slice(0, (pageAssigned.take??0)));
    setPageAssigned(prev => ({...prev, totalRecords: assignedUsers.length }));
  },[assignedUsers]);

  useEffect(()=>{
    setShownUnassignedUsers(unassignedUsers.slice(0, (pageUnassigned.take??0)));
    setPageUnassigned(prev => ({...prev, totalRecords: unassignedUsers.length}));
  },[unassignedUsers]);

  const handleAssignedUserPageChange = () => {
    var skipA = (pageAssigned.skip??0) + (pageAssigned.take??16);
    console.log(skipA);
    setPageAssigned(prev => ({...prev, skip : skipA }));
    setShownAssignedUsers(assignedUsers.slice(0, skipA + (pageAssigned.take??0)));
  };

  const handleUnassignedUserPageChange = () => {
    var skipB = (pageUnassigned.skip??0) + (pageUnassigned.take??16);
    setPageUnassigned(prev => ({...prev, skip : skipB }));
    setShownUnassignedUsers(unassignedUsers.slice(0, skipB + (pageUnassigned.take??0)));
  };

  const handlePanelSelectChange = (event : PanelBarSelectEventArguments) => {
    if(event.expandedItems.length > 0){
      const _expandedGroup : string[] = [event.target.props.title?.toString()??''];

      setExpandedGroup(_expandedGroup);
      
      var selectedUsers = groupedUnassignedUsers.find(x => x.categoryName == event.target.props.title)?.users;
      setShownUnassignedUsers(selectedUsers?.slice(0, (pageUnassigned.take??0))??[]);
      setPageUnassigned(prev => ({...prev, totalRecords: selectedUsers?.length}));
    }else{
      const _expandedGroup : string[] = [];

      setExpandedGroup(_expandedGroup);
    }
  };

  return (
    <Dialog
      title={trans.fetchLabelKeyTranslation("AddUsersTitle", "Add Users")}
      onClose={onCloseDialog}
      className="dialogAddUserAuth"
    >
      {loading && (
        <LoadingOverlay
          customStyle={{ position: "fixed", marginTop: "55px"}}
          themeColor={"light"}
          size={"medium"}
        />
      )}
      <Tooltip anchorElement={"target"} parentTitle={true} position={"bottom"}>
        <div className="userListingData" style={{ maxWidth: "720px" }}>
          <div className="row">
            <div className="col-md-6">
              <div className="userListingDataCol">
                <div className="userListingTitle fs-14 text-black-10 font-weight-bold p-b-4">
                  {trans.fetchLabelKeyTranslation(
                          "UnassignedUsersTitle",
                          "Unassigned Users"
                        )}
                </div>
                <div className="UserLiSearch p-b-5">
                  <div className="formInput">
                    <Input
                      className="form-control"
                      style={{
                        height: "32px",
                        background: "rgba(255,255,255,.6)",
                      }}
                      placeholder={trans.fetchLabelKeyTranslation(
                        "SearchUsersText",
                        "Search Users…"
                      )}
                      value={unassignedUserSearch}
                      onChange={handleUnassignedUserSearchChange}
                    />
                  </div>
                </div>
                <div id="scrollableDiv"
                  className="detailColBox scroller userListGroup"
                  style={{ height: "325px", overflow: "hidden auto" }}
                >
                    <PanelBar onSelect={handlePanelSelectChange} 
                      isControlled={true}    
                      expanded={expandedGroup}
                    >
                      {groupedUnassignedUsers.map((group, index) => {
                        return (
                          <PanelBarItem
                            key={group.categoryName}
                            id={group.categoryName}              
                            title={group.categoryName}                          
                          >
                            <InfiniteScroll
                              dataLength={shownUnassignedUsers.length}
                              next={handleUnassignedUserPageChange}
                              hasMore={pageUnassigned.totalRecords!==undefined && shownUnassignedUsers.length<pageUnassigned.totalRecords}
                              loader={<div className="col-12 text-center"><Loader/></div>}
                              scrollableTarget="scrollableDiv"
                              className="row"
                            >
                              {shownUnassignedUsers.map((user) => {
                                var _user: TinyUser = {
                                  id: user.id,
                                  loginUserId: user.loginUserId,
                                  firstName: user.firstName ?? "",
                                  lastName: user.lastName ?? "",
                                  email: user.email ?? "",
                                  phoneNo: "",
                                  image: user.image ?? "",
                                  isArchived: user.isArchived
                                };
                                return (
                                  <div className="UserRow w-100 float-left p-t-10 p-b-10 border-bottom-solid border-w-1 border-black-1">
                                    <div className="tblUsr d-flex justify-content-between w-100">
                                      <CustomUserGridInfo userInfo={_user} />
                                      <div className="userActionBtn p-r-10">
                                        <a
                                          className="cursor-pointer"
                                          onClick={() => handleAddUser(user)}
                                        >
                                          <span
                                            className="fs-25 bg-black-4 text-primary radius-50 d-flex align-items-center justify-content-center"
                                            style={{
                                              height: "30px",
                                              width: "30px",
                                            }}
                                            title={trans.fetchLabelKeyTranslation(
                                              "AddButton",
                                              "Add"
                                            )}
                                          >
                                            <i className="bi bi-arrow-right"></i>
                                          </span>
                                        </a>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </InfiniteScroll>
                          </PanelBarItem>
                        );
                      })}
                    </PanelBar>
                  
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="userListingDataCol">
                <div className="userListingTitle fs-14 text-black-10 font-weight-bold p-b-4">
                  {trans.fetchLabelKeyTranslation(
                          "AssignedUsersTitle",
                          "Group Members"
                        )}
                </div>
                <div className="UserLiSearch p-b-5">
                  <div className="formInput">
                    <Input
                      className="form-control"
                      style={{
                        height: "32px",
                        background: "rgba(255,255,255,.6)",
                      }}
                      placeholder={trans.fetchLabelKeyTranslation(
                        "SearchUsersText",
                        "Search"
                      )}
                      value={assignedUserSearch}
                      onChange={handleAssignedUserSearchChange}
                    />
                  </div>
                </div>
                <div id="scrollableDivAssigned" 
                  className="detailColBox scroller"
                  style={{ height: "290px", overflow: "hidden auto" }}
                >
                  <InfiniteScroll
                      dataLength={shownAssignedUsers.length}
                      next={handleAssignedUserPageChange}
                      hasMore={pageAssigned.totalRecords!==undefined && shownAssignedUsers.length<pageAssigned.totalRecords}
                      loader={<div className="col-12 text-center"><Loader/></div>}
                      scrollableTarget="scrollableDivAssigned"
                  >
                  {shownAssignedUsers
                    .filter((el) => searchUser(el, assignedUserSearch))
                    .map((user) => {
                      var _user: TinyUser = {
                        id: user.id,
                        loginUserId: user.loginUserId,
                        firstName: user.firstName ?? "",
                        lastName: user.lastName ?? "",
                        email: user.email ?? "",
                        phoneNo: "",
                        image: user.image ?? "",
                        isArchived: user.isArchived
                      };
                      return (
                        <div className="UserRow w-100 float-left p-t-10 p-b-10 border-bottom-solid border-w-1 border-black-1">
                          <div className="tblUsr d-flex justify-content-between w-100">
                            <CustomUserGridInfo userInfo={_user} />
                            {user.removeable !== undefined &&
                              user.removeable && (
                                <div className="userActionBtn p-r-10">
                                  <a
                                    className="cursor-pointer"
                                    onClick={() => handleDeleteUser(user)}
                                  >
                                    <span
                                      className="fs-16 bg-black-4 text-primary radius-50 d-flex align-items-center justify-content-center"
                                      style={{ height: "30px", width: "30px" }}
                                      title={trans.fetchLabelKeyTranslation(
                                        "DeleteButton",
                                        "Delete"
                                      )}
                                    >
                                      <i className="bi bi-trash3"></i>
                                    </span>
                                  </a>
                                </div>
                              )}
                          </div>
                        </div>
                      );
                    })}
                  </InfiniteScroll>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Tooltip>
      <DialogActionsBar>
        {error && (
          <span className="fs-15">
            <i className="bi bi-exclamation-triangle-fill tx-amber"></i>{" "}
            {trans.fetchLabelKeyTranslation(
                    "WrongError",
                    "Uh Oh! Something Went Wrong. Please Try Again!"
                  )}
          </span>
        )}
        <Button onClick={onCloseDialog}>{trans.fetchLabelKeyTranslation("CancelButton", "Cancel")}</Button>
        <Button
          disabled={updateButton}
          className={`btn ${!updateButton && `bg-primary text-white`}`}
          onClick={submitHandler}
        >
          {loading ? (
            <Loader
              themeColor={"primary"}
              size={"small"}
              type={"infinite-spinner"}
            />
          ) : (
            trans.fetchLabelKeyTranslation("SaveButtonText", "Save")
          )}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default UpsertProfileUsersDialog;
